import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useT } from "@/i18n/LanguageProvider";

export default function Menu({ items = [] }) {
  const { t } = useT();
  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="relative w-full bg-[#FFF8EE] py-28 md:py-40"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 max-w-5xl">
          <div>
            <p className="label-tracked text-[#7B5C4E]" data-testid="menu-eyebrow">
              {t("menu.eyebrow")}
            </p>
            <h2
              className="font-serif font-light text-[#211512] mt-6 leading-[1]"
              style={{ fontSize: "clamp(2.4rem, 5.6vw, 5rem)" }}
              data-testid="menu-headline"
            >
              {t("menu.headline_1")}
              <br />
              <span className="italic" style={{ color: "#7B5C4E" }}>
                {t("menu.headline_2")}
              </span>
            </h2>
          </div>
          <p
            className="text-[#211512]/70 max-w-sm"
            data-testid="menu-description"
          >
            {t("menu.description")}
          </p>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {items.map((item, idx) => (
            <MenuCard
              key={item.id}
              item={item}
              index={idx}
              t={t}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function MenuCard({ item, index, t }) {
  const videoRef = useRef(null);
  const cardRef = useRef(null);
  const [active, setActive] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch(
      typeof window !== "undefined" &&
        ("ontouchstart" in window || navigator.maxTouchPoints > 0)
    );
  }, []);

  useEffect(() => {
    if (!isTouch || !cardRef.current) return;
    const el = cardRef.current;
    const obs = new IntersectionObserver(
      ([entry]) => setActive(entry.intersectionRatio > 0.55),
      { threshold: [0, 0.55, 0.9] }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [isTouch]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (active) {
      v.currentTime = 0;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [active]);

  return (
    <motion.article
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.9, delay: index * 0.08, ease: [0.2, 0.8, 0.2, 1] }}
      onMouseEnter={() => !isTouch && setActive(true)}
      onMouseLeave={() => !isTouch && setActive(false)}
      data-testid={`menu-card-${item.id}`}
      className="group relative bg-white border border-[#211512]/8 overflow-hidden"
    >
      <div className="relative aspect-[4/3] sm:aspect-[16/10] overflow-hidden bg-[#211512]">
        <img
          src={item.image}
          alt={item.name}
          data-testid={`menu-image-${item.id}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            active ? "opacity-0" : "opacity-100"
          }`}
        />
        <video
          ref={videoRef}
          src={item.video}
          muted
          loop
          playsInline
          preload="metadata"
          data-testid={`menu-video-${item.id}`}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${
            active ? "opacity-100" : "opacity-0"
          }`}
        />

        {/* gold accent corner */}
        <div
          className="absolute top-6 left-6 label-tracked"
          style={{ color: item.accent || "#C5A059" }}
        >
          0{item.order}
        </div>

        <div
          className={`absolute bottom-6 right-6 px-3 py-1 label-tracked text-[#FFF8EE] transition-opacity duration-500 ${
            active ? "opacity-0" : "opacity-100"
          }`}
          style={{ background: "rgba(33,21,18,0.6)", backdropFilter: "blur(4px)" }}
        >
          Hover to preview
        </div>
      </div>

      <div className="p-6 sm:p-8 md:p-9 flex flex-col gap-4 sm:gap-5">
        <div>
          <h3
            className="font-serif text-[#211512] leading-tight"
            style={{ fontSize: "clamp(1.5rem, 2.4vw, 2.25rem)" }}
            data-testid={`menu-name-${item.id}`}
          >
            {item.name}
          </h3>
          <p className="font-serif italic text-[#7B5C4E] mt-1 text-sm sm:text-base">
            {item.tagline}
          </p>
        </div>

        <p
          className="text-[#211512]/75 text-sm leading-relaxed"
          data-testid={`menu-desc-${item.id}`}
        >
          {item.description}
        </p>

        <div className="flex items-center justify-between border-t border-[#211512]/10 pt-5 gap-4 flex-wrap">
          <div className="flex items-baseline gap-1">
            <span
              className="font-serif text-[#211512]"
              style={{ fontSize: "1.5rem" }}
              data-testid={`menu-price-${item.id}`}
            >
              ${item.price.toFixed(2)}
            </span>
            <span className="label-tracked text-[#7B5C4E] ml-2 text-[10px]">{t("menu.per_tray")}</span>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
